/**
 * Three.js Kaiba-style corporate attaché. Cards fly into an open case
 * and land face-up in a grid on the lining.
 */

import * as THREE from 'three'
import { CARD_BACK_URL } from '../../utils/cardImages'
import { loadCardImage } from '../../utils/cardImageCache'
import {
  applyWebglBudget,
  listenWebglLost,
  webglRendererOptions,
} from '../webglPhone'
import { fitFov, packFrame, type PackFrame } from './packFrame'
import './CollectionVault.css'

export interface VaultCallbacks {
  onCardLand?: (index: number) => void
  onLidClose?: () => void
  onComplete?: () => void
  /** The GPU dropped the canvas. The opener should show the card grid. */
  onContextLost?: () => void
}

interface FlyCard {
  group: THREE.Group
  delay: number
  duration: number
  start: THREE.Vector3
  mid: THREE.Vector3
  end: THREE.Vector3
  startEuler: THREE.Euler
  endEuler: THREE.Euler
  startScale: number
  endScale: number
  landed: boolean
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t
const clamp01 = (t: number): number => Math.max(0, Math.min(1, t))
const easeOut = (t: number): number => 1 - (1 - t) ** 3
const easeInOut = (t: number): number =>
  t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2

const bezier = (a: number, b: number, c: number, t: number): number => {
  const u = 1 - t
  return u * u * a + 2 * u * t * b + t * t * c
}

const CARD_W = 0.82
const CARD_H = 1.2
const CASE_W = 2.72
const CASE_D = 1.58
const CASE_H = 0.36

export class CollectionVault {
  readonly kind = 'case' as const
  private readonly root: HTMLDivElement
  private readonly canvas: HTMLCanvasElement
  private readonly renderer: THREE.WebGLRenderer
  private readonly scene: THREE.Scene
  private readonly camera: THREE.PerspectiveCamera
  private readonly lid: THREE.Group
  private readonly disposables: { dispose: () => void }[] = []
  private readonly cards: FlyCard[] = []
  private readonly callbacks: VaultCallbacks
  private raf = 0
  private unlistenGl: (() => void) | null = null
  private contextLost = false
  private startedAt = 0
  private skipped = false
  private disposed = false
  private finished = false
  private lidClosedPlayed = false
  private texturesReady: Promise<void>
  private resolvePlay: (() => void) | null = null
  private readonly resizeObs: ResizeObserver
  private readonly lidOpenAt = 0.12
  private readonly lidOpenDur = 0.65
  private readonly cardsStart = 0.45
  private readonly flyDur = 0.85
  private readonly stagger: number
  private readonly lidCloseAt: number
  private readonly lidCloseDur = 0.7
  private readonly completeAt: number
  private frame: PackFrame = 'wide'

  constructor(
    host: HTMLElement,
    cardUrls: string[],
    callbacks: VaultCallbacks = {},
    labelText = 'Collection',
  ) {
    this.callbacks = callbacks
    const n = Math.max(1, cardUrls.length)
    this.stagger = Math.min(0.12, 2.1 / n)
    const lastStart = this.cardsStart + (n - 1) * this.stagger
    this.lidCloseAt = lastStart + this.flyDur + 0.35
    this.completeAt = lastStart + this.flyDur + 1.05

    this.root = document.createElement('div')
    this.root.className = 'cv-root'
    this.canvas = document.createElement('canvas')
    this.canvas.className = 'cv-canvas'
    const label = document.createElement('p')
    label.className = 'cv-label'
    label.textContent = labelText
    this.root.append(this.canvas, label)

    try {
      this.renderer = new THREE.WebGLRenderer(webglRendererOptions(this.canvas))
    } catch (err) {
      this.canvas.remove()
      throw err
    }
    applyWebglBudget(this.renderer, 2)
    this.unlistenGl = listenWebglLost(this.canvas, this.handleContextLost)

    this.scene = new THREE.Scene()
    this.scene.fog = new THREE.Fog(0x070714, 8, 16)
    this.camera = new THREE.PerspectiveCamera(38, 1, 0.1, 40)
    this.camera.position.set(0, 2.2, 4.95)
    this.camera.lookAt(0, 0.42, 0.18)

    this.addLights()
    this.lid = this.buildSuitcase()
    this.resizeObs = new ResizeObserver(() => this.resize())

    try {
      host.appendChild(this.root)
      this.texturesReady = this.buildCards(cardUrls)
      this.resize()
      this.resizeObs.observe(this.root)
    } catch (err) {
      this.unlistenGl()
      this.unlistenGl = null
      this.resizeObs.disconnect()
      this.root.remove()
      throw err
    }
  }

  private handleContextLost = (): void => {
    if (this.disposed || this.contextLost) return
    this.contextLost = true
    if (this.raf) cancelAnimationFrame(this.raf)
    this.raf = 0
    this.callbacks.onContextLost?.()
  }

  play(): Promise<void> {
    return new Promise((resolve) => {
      if (this.disposed) {
        resolve()
        return
      }
      this.resolvePlay = resolve
      void this.texturesReady.then(() => {
        if (this.disposed) {
          resolve()
          return
        }
        this.startedAt = performance.now()
        this.tick()
      }).catch(() => {
        if (this.disposed) {
          resolve()
          return
        }
        this.startedAt = performance.now()
        this.tick()
      })
    })
  }

  skip(): void {
    if (this.disposed || this.finished) return
    this.skipped = true
  }

  dispose(): void {
    this.disposed = true
    if (this.raf) cancelAnimationFrame(this.raf)
    this.unlistenGl?.()
    this.unlistenGl = null
    this.resizeObs.disconnect()
    this.scene.traverse((obj) => {
      const mesh = obj as THREE.Mesh
      if (mesh.isMesh) {
        mesh.geometry?.dispose()
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
        for (const mat of mats) mat?.dispose()
      }
    })
    for (const item of this.disposables) item.dispose()
    this.renderer.dispose()
    this.root.remove()
    this.resolvePlay?.()
    this.resolvePlay = null
  }

  private addLights(): void {
    const ambient = new THREE.AmbientLight(0xd7e3f4, 0.72)
    const key = new THREE.DirectionalLight(0xfff7ea, 1.45)
    key.position.set(2.1, 5.4, 4.2)
    key.castShadow = true
    key.shadow.mapSize.set(1024, 1024)
    const rim = new THREE.DirectionalLight(0x60a5fa, 0.85)
    rim.position.set(-3.4, 2.6, -2.0)
    const fill = new THREE.PointLight(0xffffff, 0.9, 14)
    fill.position.set(0, 2.2, 3.5)
    const goldKick = new THREE.PointLight(0xfde68a, 0.55, 8)
    goldKick.position.set(0, 1.4, 1.2)
    this.scene.add(ambient, key, rim, fill, goldKick)
  }

  private trackGeo(geo: THREE.BufferGeometry): THREE.BufferGeometry {
    this.disposables.push(geo)
    return geo
  }

  private shell(): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial({
      color: 0xf4f7fb,
      roughness: 0.28,
      metalness: 0.38,
      map: this.makePearl(0xf4f7fb, 0xd6dee8),
    })
    this.disposables.push(mat)
    return mat
  }

  private chrome(): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial({
      color: 0xc5ccd4,
      roughness: 0.18,
      metalness: 0.95,
    })
    this.disposables.push(mat)
    return mat
  }

  private gold(): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial({
      color: 0xd4af37,
      roughness: 0.24,
      metalness: 0.88,
    })
    this.disposables.push(mat)
    return mat
  }

  private velvet(): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial({
      color: 0x0b1b3a,
      roughness: 0.92,
      metalness: 0.04,
    })
    this.disposables.push(mat)
    return mat
  }

  private navy(): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial({
      color: 0x14264a,
      roughness: 0.55,
      metalness: 0.12,
    })
    this.disposables.push(mat)
    return mat
  }

  private makePearl(a: number, b: number): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = c.height = 256
    const ctx = c.getContext('2d')
    if (!ctx) return new THREE.CanvasTexture(c)
    ctx.fillStyle = `#${a.toString(16).padStart(6, '0')}`
    ctx.fillRect(0, 0, 256, 256)
    for (let i = 0; i < 40; i++) {
      ctx.strokeStyle = `rgba(180, 196, 216, ${0.04 + (i % 5) * 0.01})`
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(0, i * 7)
      ctx.lineTo(256, i * 7 - 18)
      ctx.stroke()
    }
    for (let i = 0; i < 900; i++) {
      ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.07})`
      ctx.fillRect(Math.random() * 256, Math.random() * 256, 2, 1)
    }
    ctx.fillStyle = `#${b.toString(16).padStart(6, '0')}18`
    ctx.fillRect(0, 0, 256, 256)
    const tex = new THREE.CanvasTexture(c)
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping
    tex.repeat.set(2, 2)
    tex.colorSpace = THREE.SRGBColorSpace
    this.disposables.push(tex)
    return tex
  }

  private makeGrain(a: number, b: number): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = c.height = 256
    const ctx = c.getContext('2d')
    if (!ctx) return new THREE.CanvasTexture(c)
    ctx.fillStyle = `#${a.toString(16).padStart(6, '0')}`
    ctx.fillRect(0, 0, 256, 256)
    for (let i = 0; i < 2800; i++) {
      ctx.fillStyle = `rgba(0,0,0,${Math.random() * 0.16})`
      ctx.fillRect(Math.random() * 256, Math.random() * 256, 2, 2)
    }
    ctx.fillStyle = `#${b.toString(16).padStart(6, '0')}22`
    ctx.fillRect(0, 0, 256, 256)
    const tex = new THREE.CanvasTexture(c)
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping
    tex.repeat.set(2, 2)
    tex.colorSpace = THREE.SRGBColorSpace
    this.disposables.push(tex)
    return tex
  }

  private makeKaibaEmblem(): THREE.CanvasTexture {
    const size = 1024
    const c = document.createElement('canvas')
    c.width = c.height = size
    const ctx = c.getContext('2d')
    if (!ctx) return new THREE.CanvasTexture(c)
    const cx = 512
    const cy = 498
    ctx.fillStyle = '#0a1733'
    ctx.fillRect(0, 0, size, size)

    ctx.beginPath()
    ctx.arc(cx, cy, 448, 0, Math.PI * 2)
    ctx.fillStyle = '#0a1733'
    ctx.fill()
    ctx.lineWidth = 36
    ctx.strokeStyle = '#d4af37'
    ctx.stroke()
    ctx.beginPath()
    ctx.arc(cx, cy, 404, 0, Math.PI * 2)
    ctx.lineWidth = 8
    ctx.strokeStyle = '#f8e7a0'
    ctx.stroke()

    ctx.save()
    ctx.translate(cx, cy)
    ctx.fillStyle = '#d4af37'
    const wing = (dir: number) => {
      ctx.beginPath()
      ctx.moveTo(dir * 70, -20)
      ctx.lineTo(dir * 250, -150)
      ctx.lineTo(dir * 175, -10)
      ctx.lineTo(dir * 255, 40)
      ctx.lineTo(dir * 168, 28)
      ctx.lineTo(dir * 248, 165)
      ctx.lineTo(dir * 70, 36)
      ctx.closePath()
      ctx.fill()
    }
    wing(-1)
    wing(1)
    ctx.fillStyle = '#f8fafc'
    ctx.beginPath()
    ctx.moveTo(0, -210)
    ctx.lineTo(46, -128)
    ctx.lineTo(0, -148)
    ctx.lineTo(-46, -128)
    ctx.closePath()
    ctx.fill()
    ctx.restore()

    ctx.fillStyle = '#f8fafc'
    ctx.font = 'bold 268px Georgia, "Times New Roman", serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('KC', cx, cy + 18)
    ctx.fillStyle = '#d4af37'
    ctx.font = 'bold 46px Arial, sans-serif'
    ctx.fillText('KAIBA CORPORATION', cx, 860)

    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy()
    this.disposables.push(tex)
    return tex
  }

  private makeNameplate(): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = 1024
    c.height = 256
    const ctx = c.getContext('2d')
    if (!ctx) return new THREE.CanvasTexture(c)
    ctx.fillStyle = '#0b1b3a'
    ctx.fillRect(0, 0, 1024, 256)
    ctx.strokeStyle = '#d4af37'
    ctx.lineWidth = 14
    ctx.strokeRect(10, 10, 1004, 236)
    ctx.strokeStyle = '#f8e7a0'
    ctx.lineWidth = 3
    ctx.strokeRect(28, 28, 968, 200)
    ctx.fillStyle = '#f8fafc'
    ctx.font = 'bold 92px Georgia, "Times New Roman", serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('KAIBA CORP', 512, 118)
    ctx.fillStyle = '#d4af37'
    ctx.font = 'bold 32px Arial, sans-serif'
    ctx.fillText('DUEL SYSTEMS  ·  COLLECTION CASE', 512, 188)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    this.disposables.push(tex)
    return tex
  }

  private logoMat(tex: THREE.Texture): THREE.MeshStandardMaterial {
    const mat = new THREE.MeshStandardMaterial({
      map: tex,
      roughness: 0.32,
      metalness: 0.18,
      emissive: new THREE.Color(0xffffff),
      emissiveMap: tex,
      emissiveIntensity: 0.22,
      side: THREE.DoubleSide,
    })
    this.disposables.push(mat)
    return mat
  }

  private addCornerCaps(
    parent: THREE.Group,
    chrome: THREE.Material,
    y: number,
    zSign: number,
  ): void {
    const geo = this.trackGeo(new THREE.BoxGeometry(0.12, 0.1, 0.12))
    for (const x of [-CASE_W / 2 + 0.07, CASE_W / 2 - 0.07]) {
      const cap = new THREE.Mesh(geo, chrome)
      cap.position.set(x, y, zSign * (CASE_D / 2 - 0.07))
      cap.castShadow = true
      parent.add(cap)
    }
  }

  private buildHandle(chrome: THREE.Material, gold: THREE.Material): THREE.Group {
    const handle = new THREE.Group()
    const postGeo = this.trackGeo(new THREE.BoxGeometry(0.07, 0.16, 0.07))
    for (const x of [-0.32, 0.32]) {
      const post = new THREE.Mesh(postGeo, chrome)
      post.position.set(x, 0.08, 0)
      handle.add(post)
    }
    const bar = new THREE.Mesh(
      this.trackGeo(new THREE.BoxGeometry(0.78, 0.07, 0.09)),
      gold,
    )
    bar.position.y = 0.18
    bar.castShadow = true
    handle.add(bar)
    return handle
  }

  private buildSuitcase(): THREE.Group {
    const shell = this.shell()
    const chrome = this.chrome()
    const gold = this.gold()
    const velvet = this.velvet()
    const navy = this.navy()
    const emblem = this.makeKaibaEmblem()
    const plate = this.makeNameplate()
    const caseGroup = new THREE.Group()

    const floor = new THREE.Mesh(
      this.trackGeo(new THREE.CircleGeometry(4.2, 40)),
      new THREE.ShadowMaterial({ opacity: 0.42 }),
    )
    floor.rotation.x = -Math.PI / 2
    floor.position.y = -0.02
    floor.receiveShadow = true
    this.scene.add(floor)

    const glow = new THREE.Mesh(
      this.trackGeo(new THREE.CircleGeometry(1.7, 36)),
      new THREE.MeshBasicMaterial({
        color: 0x1e3a8a,
        transparent: true,
        opacity: 0.2,
      }),
    )
    glow.rotation.x = -Math.PI / 2
    glow.position.y = 0.01
    this.scene.add(glow)

    const body = new THREE.Mesh(
      this.trackGeo(new THREE.BoxGeometry(CASE_W, CASE_H, CASE_D)),
      shell,
    )
    body.position.y = CASE_H / 2
    body.castShadow = true
    body.receiveShadow = true
    caseGroup.add(body)

    const band = new THREE.Mesh(
      this.trackGeo(new THREE.BoxGeometry(CASE_W + 0.02, 0.045, CASE_D + 0.02)),
      navy,
    )
    band.position.y = CASE_H - 0.04
    caseGroup.add(band)

    const inner = new THREE.Mesh(
      this.trackGeo(new THREE.BoxGeometry(CASE_W - 0.14, 0.07, CASE_D - 0.14)),
      velvet,
    )
    inner.position.y = CASE_H - 0.01
    caseGroup.add(inner)

    this.addCornerCaps(caseGroup, chrome, 0.06, 1)
    this.addCornerCaps(caseGroup, chrome, 0.06, -1)
    this.addCornerCaps(caseGroup, chrome, CASE_H - 0.05, 1)
    this.addCornerCaps(caseGroup, chrome, CASE_H - 0.05, -1)

    const latchGeo = this.trackGeo(new THREE.BoxGeometry(0.28, 0.08, 0.1))
    for (const x of [-0.58, 0.58]) {
      const latch = new THREE.Mesh(latchGeo, gold)
      latch.position.set(x, CASE_H - 0.02, CASE_D / 2 + 0.04)
      latch.castShadow = true
      caseGroup.add(latch)
    }
    const lock = new THREE.Mesh(
      this.trackGeo(new THREE.BoxGeometry(0.34, 0.1, 0.12)),
      chrome,
    )
    lock.position.set(0, CASE_H - 0.03, CASE_D / 2 + 0.05)
    caseGroup.add(lock)

    const lid = new THREE.Group()
    lid.position.set(0, CASE_H, -CASE_D / 2)
    const lidMesh = new THREE.Mesh(
      this.trackGeo(new THREE.BoxGeometry(CASE_W, 0.1, CASE_D)),
      shell,
    )
    lidMesh.position.set(0, 0.05, CASE_D / 2)
    lidMesh.castShadow = true
    const lidInner = new THREE.Mesh(
      this.trackGeo(new THREE.BoxGeometry(CASE_W - 0.12, 0.035, CASE_D - 0.12)),
      velvet,
    )
    lidInner.position.set(0, -0.01, CASE_D / 2)

    const innerLogo = new THREE.Mesh(
      this.trackGeo(new THREE.CircleGeometry(0.58, 48)),
      this.logoMat(emblem),
    )
    innerLogo.position.set(0, -0.03, CASE_D / 2)
    innerLogo.rotation.x = Math.PI / 2

    const plateMesh = new THREE.Mesh(
      this.trackGeo(new THREE.PlaneGeometry(1.55, 0.38)),
      this.logoMat(plate),
    )
    plateMesh.position.set(0, 0.102, CASE_D / 2 - 0.12)
    plateMesh.rotation.x = -Math.PI / 2

    const outerMark = new THREE.Mesh(
      this.trackGeo(new THREE.CircleGeometry(0.22, 40)),
      this.logoMat(emblem),
    )
    outerMark.position.set(0, 0.102, CASE_D / 2 + 0.28)
    outerMark.rotation.x = -Math.PI / 2

    const handle = this.buildHandle(chrome, gold)
    handle.position.set(0, 0.1, CASE_D - 0.22)

    lid.add(lidMesh, lidInner, innerLogo, plateMesh, outerMark, handle)
    caseGroup.add(lid)
    this.scene.add(caseGroup)
    return lid
  }

  /**
   * Lay cards face-up in a grid on the felt, fully below the lid plane.
   */
  private gridSlot(
    n: number,
    i: number,
  ): { pos: THREE.Vector3; x: number; scale: number } {
    const cols = n <= 3 ? Math.max(1, n) : n <= 10 ? 5 : 6
    const rows = Math.ceil(n / cols)
    const innerW = CASE_W - 0.4
    const innerD = CASE_D - 0.4
    const gap = 0.038
    const cellW = (innerW - gap * Math.max(0, cols - 1)) / cols
    const cellD = (innerD - gap * Math.max(0, rows - 1)) / rows
    const scale = Math.min(cellW / CARD_W, cellD / CARD_H, 0.48) * 0.9
    const col = i % cols
    const row = Math.floor(i / cols)
    const rowCount = row === rows - 1 ? n - row * cols : cols
    const usedW = rowCount * cellW + Math.max(0, rowCount - 1) * gap
    const gridD = rows * cellD + Math.max(0, rows - 1) * gap
    const x = -usedW / 2 + cellW / 2 + col * (cellW + gap)
    const z = -gridD / 2 + cellD / 2 + row * (cellD + gap)
    const feltTop = CASE_H - 0.01 + 0.035
    return {
      x,
      scale,
      pos: new THREE.Vector3(x, feltTop + 0.008, z),
    }
  }

  private async buildCards(urls: string[]): Promise<void> {
    let backTex: THREE.Texture
    try {
      backTex = await this.loadTex(CARD_BACK_URL)
    } catch {
      backTex = this.makeGrain(0x1e3a5f, 0x0b1220)
    }
    const n = urls.length
    const plane = this.trackGeo(new THREE.PlaneGeometry(CARD_W, CARD_H))
    this.cards.length = n

    await Promise.all(
      urls.map(async (url, i) => {
        let frontTex: THREE.Texture
        try {
          frontTex = await this.loadTex(url)
        } catch {
          frontTex = this.makeGrain(0x334155, 0x1e293b)
        }
        const frontMat = new THREE.MeshStandardMaterial({
          map: frontTex,
          roughness: 0.35,
          metalness: 0.05,
          emissive: new THREE.Color(0x222222),
          emissiveMap: frontTex,
        })
        const backMat = new THREE.MeshStandardMaterial({
          map: backTex,
          roughness: 0.45,
        })
        this.disposables.push(frontMat, backMat)
        const group = new THREE.Group()
        const front = new THREE.Mesh(plane, frontMat)
        const back = new THREE.Mesh(plane, backMat)
        back.rotation.y = Math.PI
        front.castShadow = true
        group.add(front, back)
        group.visible = false
        this.scene.add(group)

        const fan = n === 1 ? 0 : (i / (n - 1)) * 2 - 1
        const spread = Math.min(2.3, 0.22 * n)
        const slot = this.gridSlot(n, i)
        this.cards[i] = {
          group,
          delay: this.cardsStart + i * this.stagger,
          duration: this.flyDur,
          start: new THREE.Vector3(fan * spread, 1.55, 3.35),
          mid: new THREE.Vector3(slot.x * 0.35, 2.2, 1.45),
          end: slot.pos,
          startEuler: new THREE.Euler(-0.12, fan * 0.18, fan * 0.08),
          endEuler: new THREE.Euler(-Math.PI / 2, 0, 0),
          startScale: 1,
          endScale: slot.scale,
          landed: false,
        }
      }),
    )
  }

  private loadTex(url: string): Promise<THREE.Texture> {
    return loadCardImage(url).then((img) => {
      const tex = new THREE.Texture(img)
      tex.colorSpace = THREE.SRGBColorSpace
      tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy()
      tex.needsUpdate = true
      this.disposables.push(tex)
      return tex
    })
  }

  private refreshFrame(): void {
    this.frame = packFrame(this.root.clientWidth, this.root.clientHeight)
  }

  /**
   * Keep the open case in view. Landscape phones are short, so the camera
   * sits further back instead of cropping the lid.
   */
  private placeCamera(settle: number): void {
    const short = this.frame === 'short'
    const portrait = this.frame === 'portrait'
    const yaw = short || portrait ? -0.05 * (1 - settle) : -0.14 * (1 - settle)
    let radius = lerp(4.95, 3.7, settle)
    let height = lerp(2.2, 3.55, settle)
    let lookY = lerp(0.42, 0.1, settle)
    let lookZ = 0.04
    let fitW = CASE_W + 1.55
    let fitH = 3.35
    if (short) {
      radius = lerp(5.25, 4.45, settle)
      height = lerp(2.45, 3.25, settle)
      lookY = lerp(0.48, 0.22, settle)
      fitW = CASE_W + 1.2
      fitH = 2.55
    } else if (portrait) {
      radius = lerp(5.7, 5.05, settle)
      height = lerp(3.05, 4.05, settle)
      lookY = lerp(0.55, 0.28, settle)
      fitW = CASE_W + 0.55
      fitH = 4.1
    }
    this.camera.position.set(
      Math.sin(yaw) * radius,
      height,
      Math.cos(yaw) * radius,
    )
    const look = new THREE.Vector3(0, lookY, lookZ)
    this.camera.lookAt(look)
    const dist = this.camera.position.distanceTo(look)
    this.camera.fov = fitFov(this.camera.aspect, dist, fitW, fitH, 30, 58)
    this.camera.updateProjectionMatrix()
  }

  private resize(): void {
    const w = Math.max(1, this.root.clientWidth)
    const h = Math.max(1, this.root.clientHeight)
    this.refreshFrame()
    this.camera.aspect = w / h
    this.placeCamera(0)
    this.renderer.setSize(w, h, false)
  }

  private tick = (): void => {
    if (this.disposed || this.contextLost) return
    const elapsed = (performance.now() - this.startedAt) / 1000
    const t = this.skipped ? this.completeAt : elapsed

    const openT = easeOut(clamp01((t - this.lidOpenAt) / this.lidOpenDur))
    const lidAngle = lerp(0, -2.05, openT)
    this.lid.rotation.x = lidAngle
    if (t >= this.lidCloseAt && !this.lidClosedPlayed) {
      this.lidClosedPlayed = true
      this.callbacks.onLidClose?.()
    }

    const settle = easeInOut(
      clamp01((t - this.cardsStart) / Math.max(0.2, this.completeAt - this.cardsStart)),
    )
    this.placeCamera(settle)

    for (let i = 0; i < this.cards.length; i++) {
      const card = this.cards[i]
      if (!card) continue
      const local = clamp01((t - card.delay) / card.duration)
      if (t < card.delay - 0.02) {
        card.group.visible = false
        continue
      }
      card.group.visible = true
      const p = easeInOut(local)
      card.group.position.set(
        bezier(card.start.x, card.mid.x, card.end.x, p),
        bezier(card.start.y, card.mid.y, card.end.y, p),
        bezier(card.start.z, card.mid.z, card.end.z, p),
      )
      card.group.rotation.set(
        lerp(card.startEuler.x, card.endEuler.x, p),
        lerp(card.startEuler.y, card.endEuler.y, p),
        lerp(card.startEuler.z, card.endEuler.z, p),
      )
      const s = lerp(card.startScale, card.endScale, p)
      card.group.scale.setScalar(s)
      if (local >= 1 && !card.landed) {
        card.landed = true
        if (!this.skipped) this.callbacks.onCardLand?.(i)
      }
    }

    this.renderer.render(this.scene, this.camera)
    if (this.contextLost) return
    if (t >= this.completeAt) {
      this.finish()
      return
    }
    this.raf = requestAnimationFrame(this.tick)
  }

  private finish(): void {
    if (this.finished || this.disposed) return
    this.finished = true
    this.lid.rotation.x = -2.05
    this.renderer.render(this.scene, this.camera)
    this.callbacks.onComplete?.()
    this.resolvePlay?.()
    this.resolvePlay = null
  }
}
